import type { IEmployeeTreeDepartment, IEmployeeTreeItem } from '@/modules/apiStore/types/employees/IEmployeeTree'
import type { IChatMatrixParticipant } from '@/modules/chat-matrix-fast/types/chatEmployee'

/** Оставляет в дереве только сотрудников с matrix_user_id */
export function filterEmployeesTreeByMatrixUserId(
  departments: IEmployeeTreeDepartment[],
): IEmployeeTreeDepartment[] {
  return departments
    .map(dept => ({
      ...dept,
      employees: (dept.employees ?? []).filter(employee => employee.matrix_user_id),
      children: filterEmployeesTreeByMatrixUserId(dept.children ?? []),
    }))
    .filter(dept => (dept.employees?.length ?? 0) > 0 || (dept.children?.length ?? 0) > 0)
}

export function filterEmployeesTreeExcludingKeys(
  departments: IEmployeeTreeDepartment[],
  excludeKeys: Set<string>,
): IEmployeeTreeDepartment[] {
  if (excludeKeys.size === 0) {
    return departments
  }

  return departments
    .map(dept => ({
      ...dept,
      employees: (dept.employees ?? []).filter(employee => {
        if (excludeKeys.has(String(employee.id))) {
          return false
        }
        const matrixUserId = employee.matrix_user_id?.trim()
        return !matrixUserId || !excludeKeys.has(matrixUserId)
      }),
      children: filterEmployeesTreeExcludingKeys(dept.children ?? [], excludeKeys),
    }))
    .filter(dept => (dept.employees?.length ?? 0) > 0 || (dept.children?.length ?? 0) > 0)
}

export function countEmployeesInTree(departments: IEmployeeTreeDepartment[]): number {
  return departments.reduce((total, dept) => {
    return total
      + (dept.employees?.length ?? 0)
      + countEmployeesInTree(dept.children ?? [])
  }, 0)
}

export function treeItemToChatParticipant(employee: IEmployeeTreeItem): IChatMatrixParticipant | null {
  const matrixUserId = String(employee.matrix_user_id ?? '').trim()
  if (!matrixUserId) {
    return null
  }

  return {
    id: String(employee.id),
    full_name: employee.full_name,
    position_name: employee.position_name,
    matrix_user_id: matrixUserId,
  }
}

function flattenEmployeeTree(
  departments: IEmployeeTreeDepartment[],
): IEmployeeTreeItem[] {
  const result: IEmployeeTreeItem[] = []

  for (const department of departments) {
    if (department.employees?.length) {
      result.push(...department.employees)
    }
    if (department.children?.length) {
      result.push(...flattenEmployeeTree(department.children))
    }
  }

  return result
}

export function findEmployeeInTreeByMatrixUserId(
  departments: IEmployeeTreeDepartment[],
  matrixUserId: string,
): IEmployeeTreeItem | undefined {
  const normalized = matrixUserId.trim()
  if (!normalized) {
    return undefined
  }

  return flattenEmployeeTree(departments).find(
    employee => employee.matrix_user_id?.trim() === normalized,
  )
}

export function findEmployeeInTreeById(
  departments: IEmployeeTreeDepartment[],
  employeeId: string,
): IEmployeeTreeItem | undefined {
  const normalized = String(employeeId).trim()
  if (!normalized) {
    return undefined
  }

  return flattenEmployeeTree(departments).find(
    employee => String(employee.id) === normalized,
  )
}
